/* eslint-disable camelcase */
/**
 * @author Daniel Aguirre
 * @file saga.js
 * @description EducationalCategory Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Services
import educationalCategoryService from '@nephro/services/educational-category-service';

// Constants
import {
  CREATE_EDUCATIONAL_CATEGORY,
  DELETE_EDUCATIONAL_CATEGORY_BY_ID,
  GET_ALL_EDUCATIONAL_CATEGORIES,
  GET_EDUCATIONAL_CATEGORY_BY_ID,
  UPDATE_EDUCATIONAL_CATEGORY
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get educationalCategories from backend
 * @param { object } action - Action with payload data
 */
export function* getEducationalCategoriesArray(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;
  try {
    const response = yield call(
      educationalCategoryService.getEducationalCategories,
      paged,
      limit,
      page
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get educationalCategory info by id
 * @param { object } action - Action with payload data
 */
export function* getEducationalCategoryById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(
      educationalCategoryService.getEducationalCategoryById,
      id
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.educationalCategoryNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to create educationalCategory
 * @param { object } action - Action with payload data
 */
export function* createEducationalCategory(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { name, description, parent } = data;

  try {
    const response = yield call(
      educationalCategoryService.createEducationalCategory,
      name,
      description,
      parent
    );
    yield put(
      setResponse(
        'success',
        'new educationalCategory',
        'educationalMaterial.itemCreated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update educationalCategory
 * @param { object } action - Action with payload data
 */
export function* updateEducationalCategory(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id_education_category, name, description, parent, id } = data;

  try {
    const response = yield call(
      educationalCategoryService.updateEducationalCategory,
      id_education_category,
      name,
      description,
      parent,
      id
    );
    yield put(
      setResponse(
        'success',
        'updated educationalCategory',
        'educationalMaterial.itemUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete educationalCategory by id
 * @param { object } action - Action with payload data
 */
export function* deleteEducationalCategoryById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(
      educationalCategoryService.deleteEducationalCategoryById,
      id
    );
    yield put(
      setResponse(
        'success',
        'deleted educationalCategory',
        'educationalMaterial.itemDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchEducationalCategorySaga() {
  yield takeLatest(
    GET_ALL_EDUCATIONAL_CATEGORIES,
    getEducationalCategoriesArray
  );
  yield takeLatest(GET_EDUCATIONAL_CATEGORY_BY_ID, getEducationalCategoryById);
  yield takeLatest(CREATE_EDUCATIONAL_CATEGORY, createEducationalCategory);
  yield takeLatest(UPDATE_EDUCATIONAL_CATEGORY, updateEducationalCategory);
  yield takeLatest(
    DELETE_EDUCATIONAL_CATEGORY_BY_ID,
    deleteEducationalCategoryById
  );
}
